/* eslint-disable jira/filename-case */
import omit from 'lodash/omit';
import getMeta from '@jira/platform__utils-get-meta/src/utils/get-meta/index.tsx';
import stripQueryParams from '@atlassian/jira-common-strip-query-params/src/index.tsx';
import { renderFlags } from './flags.tsx';
import renderDwellTimeComponent from './render-dwell-time/index.tsx';

const renderEngagementMessages = () => {
	import(/* webpackChunkName: "async-engagement-messages" */ './engagement-messages.tsx');
};

const renderProfileCardNextLegacy = () => {
	import(/* webpackChunkName: "async-profilecard" */ './profilecard-next-legacy.tsx');
};

const renderHeartbeat = () => {
	import(/* webpackChunkName: "async-heartbeat" */ './heartbeat.tsx');
};

const loadUpFlowAPI = () => import(/* webpackChunkName: "async-upflow" */ './upflow-global.tsx');

const installConnectModules = () =>
	import(/* webpackChunkName: "async-connect-modules-new" */ './connect-modules.tsx');

const markSafeForAnalytics = (str: null | string) => str || null;

const renderSearchDwellTimeComponent = () => {
	const searchSessionId = getMeta('ajs-search-session-id');
	const containerId = getMeta('ajs-search-container-id');
	const objectId = getMeta('ajs-search-object-id');
	const objectType = getMeta('ajs-search-content-type');
	stripQueryParams(['searchSessionId', 'searchObjectId', 'searchContainerId', 'searchContentType']);

	renderDwellTimeComponent('search-dwell-time', markSafeForAnalytics(searchSessionId), {
		containerId: markSafeForAnalytics(containerId),
		objectId: markSafeForAnalytics(objectId),
		objectType: markSafeForAnalytics(objectType),
		actionSubject: markSafeForAnalytics(objectType),
		source: markSafeForAnalytics(objectType),
	});
};

export type HighPriorityAppsArgs = {
	flags: boolean;
	navigation: boolean;
	renderNavigation: () => any;
	engagementMessages?: boolean;
};

export type LowPriorityAppsArgs = {
	upflow: boolean;
	heartbeat: boolean;
	profileCardNextLegacy: boolean;
	connectModules: boolean;
	originTracing: boolean;
	engagementMessages: boolean;
	searchDwellTimeComponent: boolean;
};

export type GlobalArgs = HighPriorityAppsArgs & LowPriorityAppsArgs;

const installHighPriorityApps = (args: HighPriorityAppsArgs) => {
	// @ts-expect-error - TS2304 - Cannot find name 'JiraContextContainer'.
	const priorityApps: Array<JiraContextContainer | any> = [];
	if (args.flags) priorityApps.push(renderFlags());
	if (args.navigation) priorityApps.push(args.renderNavigation());
	return priorityApps;
};

const installLowPriorityApps = (args: LowPriorityAppsArgs) => {
	if (args.upflow) {
		loadUpFlowAPI();
	}

	if (args.profileCardNextLegacy) {
		renderProfileCardNextLegacy();
	}
	if (args.connectModules) {
		installConnectModules();
	}
	renderEngagementMessages();

	if (args.heartbeat) {
		renderHeartbeat();
	}
	if (args.searchDwellTimeComponent) {
		renderSearchDwellTimeComponent();
	}
};

const installAppsWithPriorityOrder = async (args: GlobalArgs) => {
	await Promise.all(
		installHighPriorityApps({
			flags: args.flags,
			navigation: args.navigation,
			renderNavigation: args.renderNavigation,
		}),
	);

	installLowPriorityApps(omit(args, ['flags', 'navigation', 'renderNavigation']));
};

const defaultArgs = {
	flags: true,
	navigation: true,
	upflow: true,
	profileCardNextLegacy: true,
	heartbeat: true,
	connectModules: true,
	engagementMessages: true,
	searchDwellTimeComponent: true,
	renderNavigation: () => null,
} as const;

const globalCore: {
	install: (args: Partial<GlobalArgs>) => void;
	installHighPriorityApps: (args: HighPriorityAppsArgs) => void;
	installLowPriorityApps: (args: LowPriorityAppsArgs) => void;
} = {
	install: (args: Partial<GlobalArgs>): void => {
		const argsWithDefaults = {
			...defaultArgs,
			...args,
		};

		// @ts-expect-error - TS2345 - Argument of type '{ flags: boolean; navigation: boolean; renderNavigation: (() => any) | (() => null); upflow: boolean; heartbeat: boolean; profileCardNextLegacy: boolean; connectModules: boolean; originTracing?: boolean | undefined; engagementMessages: boolean; searchDwellTimeComponent: boolean; }' is not assignable to parameter of type 'GlobalArgs'.
		installAppsWithPriorityOrder(argsWithDefaults);
	},
	installHighPriorityApps: (args: HighPriorityAppsArgs): void => {
		const { flags, navigation, renderNavigation } = {
			...defaultArgs,
			...args,
		};

		installHighPriorityApps({ flags, navigation, renderNavigation });
	},
	installLowPriorityApps: (args: LowPriorityAppsArgs): void => {
		const installArgs = omit(
			{
				...defaultArgs,
				...args,
			},
			['flags', 'navigation', 'renderNavigation'],
		);

		installLowPriorityApps(installArgs);
	},
};

export default globalCore;
